export const PERMISSIONS = {
  DataTablesRead: 'datatables:read',
  ChargesRead: 'charges:read',
  ChargesAll: 'charges:all',
  PackagingIndex: 'packaging:index',
  PackagingAll: 'packaging:all',
  CustomerSpecsIndex: 'customerspecs:index',
  CustomerSpecsAll: 'customerspecs:all',
};

function canonicalizeRole(role) {
  return String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '.')
    .replace(/-+/g, '.')
    .replace(/\.+/g, '.');
}

// Roles → permissions mapping. The server is the source of truth for
// permissions (see api-server/server.js rolesToPermissions); this mirrors that
// logic for UI gating. Tenant tiering (home vs sister-guest vs sister-default)
// is resolved server-side and reflected in user.roles — for sister-non-guests
// the server synthesizes ['packaging.user', 'customerspecs.user']; for
// sister-guests it returns the guest's home-tenant App Role assignments.
export function getEffectivePermissions(user, opts = {}) {
  const mockAdmin = opts.mockAdmin === true;

  const rawRoles = Array.isArray(user?.roles) ? user.roles : [];
  const roles = rawRoles.map(canonicalizeRole).filter(Boolean);

  const perms = new Set();

  const isAdminRole =
    roles.includes('admin') ||
    roles.includes('thevault.admin') ||
    roles.includes('thevaultadmin') ||
    roles.includes('thevault:admin') ||
    roles.includes('thevault/admin');

  const isAdmin = mockAdmin || isAdminRole;
  if (isAdmin) {
    perms.add(PERMISSIONS.DataTablesRead);
    perms.add(PERMISSIONS.ChargesRead);
    perms.add(PERMISSIONS.ChargesAll);
    perms.add(PERMISSIONS.PackagingIndex);
    perms.add(PERMISSIONS.PackagingAll);
    perms.add(PERMISSIONS.CustomerSpecsIndex);
    perms.add(PERMISSIONS.CustomerSpecsAll);
    return { isAdmin: true, roles, permissions: perms };
  }

  const has = (r) => roles.includes(r);

  if (has('viewer')) perms.add(PERMISSIONS.PackagingIndex);
  if (has('datatables.editor')) perms.add(PERMISSIONS.DataTablesRead);
  if (has('charges.user')) perms.add(PERMISSIONS.ChargesRead);
  if (has('charges.editor')) {
    perms.add(PERMISSIONS.ChargesRead);
    perms.add(PERMISSIONS.ChargesAll);
  }

  if (has('packaging.user')) perms.add(PERMISSIONS.PackagingIndex);
  if (has('packaging.editor')) {
    perms.add(PERMISSIONS.PackagingAll);
    perms.add(PERMISSIONS.PackagingIndex);
  }

  if (has('customerspecs.user')) perms.add(PERMISSIONS.CustomerSpecsIndex);
  if (has('customerspecs.editor')) {
    perms.add(PERMISSIONS.CustomerSpecsAll);
    perms.add(PERMISSIONS.CustomerSpecsIndex);
  }

  return { isAdmin: false, roles, permissions: perms };
}

export function hasAnyPermission(permissionSet, required = []) {
  const needs = Array.isArray(required) ? required : [required];
  if (!needs.length) return true;
  const perms = permissionSet instanceof Set ? permissionSet : new Set(permissionSet || []);
  return needs.some(p => perms.has(p));
}

export function filterNavGroups(navGroups, effective) {
  const isAdmin = Boolean(effective && typeof effective === 'object' && effective.isAdmin);
  const perms =
    effective instanceof Set
      ? effective
      : (effective && typeof effective === 'object' && effective.permissions instanceof Set)
        ? effective.permissions
        : new Set(effective || []);

  const can = (...needed) => hasAnyPermission(perms, needed.flat());

  return (navGroups || [])
    .map(g => {
      if (!g?.key) return null;

      if (g.key === 'home') return g;

      if (g.key === 'settings') {
        // Preserve current behavior: non-admin users don't see Settings.
        return isAdmin ? g : null;
      }

      if (g.key === 'datatables') {
        return can(PERMISSIONS.DataTablesRead) ? g : null;
      }

      if (g.key === 'charges') {
        if (!can(PERMISSIONS.ChargesRead, PERMISSIONS.ChargesAll)) return null;
        const items = (g.items || []).filter(it => {
          if (it.path === '/charge-by-style') return can(PERMISSIONS.ChargesAll);
          return true;
        });
        return { ...g, items };
      }

      if (g.key === 'packaging') {
        if (!can(PERMISSIONS.PackagingIndex, PERMISSIONS.PackagingAll)) return null;
        const items = (g.items || []).filter(it => {
          if (it.path === '/package-index') return can(PERMISSIONS.PackagingIndex, PERMISSIONS.PackagingAll);
          return can(PERMISSIONS.PackagingAll);
        });
        return { ...g, items };
      }

      if (g.key === 'customer-specs') {
        if (!can(PERMISSIONS.CustomerSpecsIndex, PERMISSIONS.CustomerSpecsAll)) return null;
        const items = (g.items || []).filter(it => {
          if (it.path === '/CustomerSpecs/Index') return can(PERMISSIONS.CustomerSpecsIndex, PERMISSIONS.CustomerSpecsAll);
          return can(PERMISSIONS.CustomerSpecsAll);
        });
        return { ...g, items };
      }

      return null;
    })
    .filter(Boolean)
    .filter(g => g.key === 'home' || (Array.isArray(g.items) ? g.items.length > 0 : true));
}

